import { useCallback, useEffect, useState } from "react";

// Drop-in zamjena za `useState` koja state-u dodaje URL `?param=` sync.
// Tipičan use case je view toggle (`?view=table|grid`) ili filter koji
// želimo preživjeti reload + share kroz URL.
//
// API mimika `useState`-a — `[value, setValue] = useQueryParam(...)`:
//   const [view, setView] = useQueryParam("view", "table", ["table", "grid"] as const);
//
// - `paramName` — ime query parametra u URL-u
// - `defaultValue` — fallback ako parametar ne postoji ili je nevažeći
// - `allowedValues` — opcionalna whitelist; ako je zadan, vrijednost izvan
//    skupa fallback-a na `defaultValue` (init + popstate). Bez whitelist-a
//    se prihvaća bilo koji string iz URL-a.
//
// Implementacija:
// - Setter koristi `history.replaceState` (ne pushState) jer ne želimo da
//   svaki klik na toggle stvori novu history entry — back gumb bi se inače
//   pretvorio u undo za toggle.
// - Popstate listener vraća state u sync s URL-om kad korisnik koristi
//   browser back/forward.
const useQueryParam = <T extends string>(
  paramName: string,
  defaultValue: T,
  allowedValues?: readonly T[]
): [T, (value: T) => void] => {
  const read = useCallback((): T => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(paramName);
    if (raw == null) return defaultValue;
    if (allowedValues && !allowedValues.includes(raw as T)) return defaultValue;
    return raw as T;
  }, [paramName, defaultValue, allowedValues]);

  const [value, setStateValue] = useState<T>(read);

  const setValue = useCallback(
    (next: T) => {
      setStateValue(next);
      const params = new URLSearchParams(window.location.search);
      if (params.get(paramName) === next) return;
      params.set(paramName, next);
      window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
    },
    [paramName]
  );

  useEffect(() => {
    const onPopState = () => {
      const next = read();
      setStateValue((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [read]);

  return [value, setValue];
};

export default useQueryParam;
